'use client';

import { c as _c } from "react/compiler-runtime";
import { FixedToBody } from '@morpho-org/ui-v6/client';
import { cn } from '@morpho-org/ui-v6/utils';
import { PrefetchKind } from 'next/dist/client/components/router-reducer/router-reducer-types';
import NextLink from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { type ComponentProps, type MouseEventHandler, useRef, useState } from 'react';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { getUserSettingsCookie } from '@/helpers/cookieStorage/userSettings/userSettingsCookie.client';
import { useNetworkNavigation } from '@/hooks/useNetworkNavigation';
import './InternalLink.css';
const FILTER_PAGE_KEYS: Record<string, PageKey> = {
  '/vaults': PageKey.Earn,
  '/variable': PageKey.Borrow
};

/** Delay before a hovered link starts prefetching, for `prefetch="on-hover-debounced"`. */
const PREFETCH_HOVER_DEBOUNCE_MS = 150;

/** Append saved filter query string from cookie when navigating to /vaults or /variable. */
function withSavedFilters(pathName: string): string {
  if (pathName.includes('?')) return pathName;
  const pageKey = FILTER_PAGE_KEYS[pathName];
  if (!pageKey) return pathName;
  const cookie = getUserSettingsCookie();
  const saved = cookie?.[pageKey]?.['filters'];
  return typeof saved === 'string' && saved.startsWith('?') ? `${pathName}${saved}` : pathName;
}
export interface PrefetchProps {
  prefetch?: boolean | 'auto' | null | 'on-hover' | 'on-hover-debounced';
}
export interface InternalLinkProps extends Omit<ComponentProps<typeof NextLink>, 'prefetch'>, PrefetchProps {
  shouldOpenLinkInNewTab?: boolean;
  withHoverEffect?: boolean;
}
const InternalLink = t0 => {
  const $ = _c(32);
  const {
    href,
    shouldOpenLinkInNewTab,
    withHoverEffect: t1,
    style,
    onClick,
    prefetch: t2,
    ...props
  } = t0;
  const withHoverEffect = t1 === undefined ? false : t1;
  const prefetch = t2 === undefined ? "on-hover" : t2;
  const [isNavigating, setIsNavigating] = useState(false);
  const {
    navigateTo
  } = useNetworkNavigation();
  const path = usePathname();
  const router = useRouter();
  const hasPrefetched = useRef(false);
  const prefetchTimer = useRef(undefined);
  const [navigationStartPath, setNavigationStartPath] = useState(path);
  if (navigationStartPath !== path) {
    setNavigationStartPath(path);
    setIsNavigating(false);
  }
  let t3;
  if ($[0] !== href || $[1] !== navigateTo || $[2] !== onClick || $[3] !== path || $[4] !== shouldOpenLinkInNewTab) {
    t3 = e => {
      const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1;
      if (!isModifiedClick && !shouldOpenLinkInNewTab) {
        e.preventDefault();
        const target = withSavedFilters(href.toString());
        const isSamePathname = new URL(target, window.location.href).pathname === path;
        navigateTo(target, isSamePathname ? undefined : () => setIsNavigating(true));
      }
      onClick?.(e);
    };
    $[0] = href;
    $[1] = navigateTo;
    $[2] = onClick;
    $[3] = path;
    $[4] = shouldOpenLinkInNewTab;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const onNavigate = t3;
  let t4;
  if ($[6] !== href || $[7] !== router) {
    t4 = () => {
      if (hasPrefetched.current) {
        return;
      }
      hasPrefetched.current = true;
      router.prefetch(href.toString(), {
        kind: PrefetchKind.FULL
      });
    };
    $[6] = href;
    $[7] = router;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const prefetchLink = t4;
  let t5;
  if ($[9] !== isNavigating) {
    t5 = isNavigating && <FixedToBody top={0} left={0} portalContainerId="fixed"><div className="navigation-progress-bar" /></FixedToBody>;
    $[9] = isNavigating;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const T0 = NextLink;
  const t6 = cn(withHoverEffect && "h-lg gap-2xs pr-sm hover:bg-accent hover:text-foreground relative inline-flex items-center rounded-lg pl-[7px] text-inherit no-underline", props.className);
  const t7 = prefetch === "on-hover" || prefetch === "on-hover-debounced" ? false : prefetch;
  let t8;
  if ($[11] !== prefetch || $[12] !== prefetchLink || $[13] !== props) {
    t8 = prefetch === "on-hover" ? e_0 => {
      props.onMouseEnter?.(e_0);
      prefetchLink();
    } : prefetch === "on-hover-debounced" ? e_1 => {
      props.onMouseEnter?.(e_1);
      prefetchTimer.current = setTimeout(prefetchLink, PREFETCH_HOVER_DEBOUNCE_MS);
    } : props.onMouseEnter;
    $[11] = prefetch;
    $[12] = prefetchLink;
    $[13] = props;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== prefetch || $[16] !== props) {
    t9 = prefetch === "on-hover-debounced" ? e_2 => {
      props.onMouseLeave?.(e_2);
      clearTimeout(prefetchTimer.current);
    } : props.onMouseLeave;
    $[15] = prefetch;
    $[16] = props;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = shouldOpenLinkInNewTab ? "_blank" : undefined;
  let t11;
  if ($[18] !== T0 || $[19] !== href || $[20] !== onNavigate || $[21] !== props || $[22] !== style || $[23] !== t10 || $[24] !== t6 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t11 = <T0 {...props} className={t6} prefetch={t7} onMouseEnter={t8} onMouseLeave={t9} href={href} onClick={onNavigate} target={t10} style={style} />;
    $[18] = T0;
    $[19] = href;
    $[20] = onNavigate;
    $[21] = props;
    $[22] = style;
    $[23] = t10;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11 || $[30] !== t5) {
    t12 = <>{t5}{t11}</>;
    $[29] = t11;
    $[30] = t5;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
export default InternalLink;